/* eslint-disable react/prop-types */

import {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {motion, AnimatePresence} from 'framer-motion';
import {useForm} from 'react-hook-form';
import {useClickAway} from 'react-use';

import {EditDocumentContainer} from './elements';
import {Footer, EditForm} from './components';
import CloseSidebarIcon from '../../../../../../assets/icons/close-modal.svg';
import {useErrorMessage} from '../../../../../../utils/hooks';
import {
  setError as setServerError,
  adminLibrarySelector,
  updateLibraryDocument,
} from '../../../../../../redux/adminLibrarySlice';
import {showToast} from '../../../../../../components/common/Toast';
import UpdateIcon from '../../../../../../assets/icons/updateadmintoast.svg';

const FIELDS_IN_ORDER = [
  'documentTitle',
  ' fileCode',
  'fileStatus',
  'className',
  'fileCategory',
  'publicLibrary',
];

export const EditDocument = ({
  isVisible,
  onClose,
  editDocument,
  isPublic,
  onSuccess,
}) => {
  const {
    error: serverError,
    orgUnitInstance,
    isAdminLibraryLoading,
    activeClasses,
    formError,
    category,
  } = useSelector(adminLibrarySelector);
  const dispatch = useDispatch();
  const {
    control,
    formState: {errors},
    getValues,
    setValue,
    watch,
    register,
    setError,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues: {
      documentTitle: '',
      fileCode: '',
      fileStatus: null,
      className: null,
      fileCategory: null,
      publicLibrary: false,
    },
  });

  const clearServerError = useCallback(() => {
    dispatch(setServerError(null));
  }, [dispatch]);

  useErrorMessage(errors, serverError, clearServerError, FIELDS_IN_ORDER);

  const [uploadDocument, setUploadDocument] = useState(null);
  const [uploadCoverImage, setUploadCoverImage] = useState(null);
  const [isEditCoverImage, setIsEditCoverImage] = useState(false);
  const [fileStatus, setFileStatus] = useState(
    !isPublic
      ? {
          label: 'Add to a Class',
          value: 0,
        }
      : {
          label: 'Public',
          value: 1,
        },
  );
  const [className, setClassName] = useState(
    !isPublic
      ? {
          label: `${editDocument?.orgUnitInstance?.class?.oc_class} - ${editDocument?.orgUnitInstance?.orgUnit?.unit?.tun_title}`,
          value: editDocument?.orgUnitInstance?.orui_id_orgunitinstance,
        }
      : null,
  );

  const [selectCategory, setSelectCategory] = useState(null);
  const categoryDropdown = useRef();
  const ref = useRef(null);
  useClickAway(ref, () => {
    onClose();
  });

  useEffect(() => {
    setUploadDocument({
      type: editDocument?.fileType?.ft_type,
      name: editDocument?.fi_filename,
      location: editDocument?.fi_location,
    });
    if (editDocument?.fi_thumbnail) {
      setUploadCoverImage({
        type: 'image',
        name: editDocument?.fi_filename,
        location: editDocument?.fi_cover_image
          ? editDocument?.fi_cover_image
          : editDocument?.fi_thumbnail,
      });
    }
    setSelectCategory([
      {
        name: editDocument?.fileCategory?.fc_name,
        id: editDocument?.fileCategory?.fc_id_filecategory,
      },
    ]);
    setValue('documentTitle', editDocument?.fi_text_content);
    setValue('fileCode', editDocument?.fi_code);
    setValue('fileCategory', {
      id: editDocument?.fileCategory?.fc_id_filecategory,
      name: editDocument?.fileCategory?.fc_name,
    });
    setValue('fileStatus', fileStatus);
    setValue('className', className);
  }, [className, editDocument, fileStatus, setValue]);

  const setFieldError = (fieldName, errorMessage) => {
    setError(fieldName, {message: errorMessage});
  };

  const onSubmit = data => {
    if (!data?.documentTitle) {
      setFieldError('documentTitle', 'Enter Document Title');
    }
    if (!data?.fileCode) {
      setFieldError('fileCode', 'Enter Code');
    }
    if (!data?.fileStatus?.value) {
      setFieldError('fileStatus', 'Select the File type');
    }
    if (data?.fileStatus?.value === 0) {
      clearErrors('fileStatus');
      if (!data?.className) {
        setFieldError('className', 'Select the Class Name');
      }
    }
    if (!data?.fileCategory) {
      setFieldError('fileCategory', 'Select the Category');
    }

    const formData = new FormData();
    if (data?.documentTitle || data?.fileCode || data?.fileCategory) {
      formData.append('documentTitle', data?.documentTitle);

      formData.append('fileStatus', data?.fileStatus?.value);
      formData.append('fileCode', data?.fileCode);
      formData.append('category', data?.fileCategory?.id);

      if (data?.fileStatus?.value === '0') {
        formData.append('className', data?.className?.value);
      }

      if (isEditCoverImage) {
        if (uploadCoverImage) {
          formData.append(
            'uploadCoverImage',
            uploadCoverImage,
            uploadCoverImage?.name,
          );
        }
      }
      formData.append('isEditCoverImage', isEditCoverImage);

      const orgUnitInstanceId = orgUnitInstance?.orui_id_orgunitinstance;
      if (
        !isAdminLibraryLoading &&
        !formError?.fileCode &&
        data?.fileCategory !== null
      ) {
        dispatch(
          updateLibraryDocument(
            orgUnitInstanceId,
            editDocument?.fi_id_file,
            formData,
            () => {
              showToast(UpdateIcon, 'Document updated successfully', false);
              setUploadDocument(null);
              setUploadCoverImage(null);
              onSuccess();
              onClose();
            },
          ),
        );
      }
    }
  };
  useEffect(() => {
    const bodyScrollBar = document.body;
    bodyScrollBar.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    getValues('documentTitle');
    getValues('fileCategory');
  }, [getValues]);

  const fileStatusOptions = [
    {label: 'Public', value: 1},
    {label: 'Add to a Class', value: 0},
  ];

  const classesAsOptions = useMemo(
    () =>
      activeClasses?.map(item => ({
        label: `${item?.class?.oc_class} - ${item?.orgUnit?.unit?.tun_title}`,
        value: item?.orui_id_orgunitinstance,
      })) || [],

    [activeClasses],
  );

  const categoryAsOptions = useMemo(
    () =>
      category?.map(item => ({
        name: `${item?.fc_name}`,
        id: item?.fc_id_filecategory,
      })) || [],

    [category],
  );

  return (
    <AnimatePresence>
      {isVisible ? (
        <EditDocumentContainer
          className="d-flex justify-content-end position-fixed"
          animate={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
          exit={{
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
          }}
          initial={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
          transition={{
            duration: 0.4,
            type: 'keyframes',
          }}
        >
          <motion.aside
            className="sidebar"
            animate={{
              x: '0',
            }}
            exit={{
              x: '100%',
            }}
            initial={{
              x: '100%',
            }}
            transition={{
              duration: 0.4,
              type: 'keyframes',
            }}
            ref={ref}
          >
            <header className="header-title align-items-center d-flex justify-content-between mb-4 fixed-top">
              <div className="sidebar-head d-flex">
                <div className="sidebar-title">Edit a File</div>
                {!isPublic ? (
                  <div className="class-name">{`${editDocument?.orgUnitInstance?.class?.oc_class} - ${editDocument?.orgUnitInstance?.orgUnit?.unit?.tun_title}`}</div>
                ) : null}
              </div>
              <button
                className="close-sidebar-button"
                onClick={onClose}
                type="button"
                id="close-sidebar"
              >
                <img alt="Close Sidebar" src={CloseSidebarIcon} />
              </button>
            </header>
            <EditForm
              uploadDocument={uploadDocument}
              uploadCoverImage={uploadCoverImage}
              setUploadCoverImage={setUploadCoverImage}
              onClose={onClose}
              register={register}
              setValue={setValue}
              categoryDropdown={categoryDropdown}
              errors={errors}
              categoryAsOptions={categoryAsOptions}
              control={control}
              fileStatusOptions={fileStatusOptions}
              classesAsOptions={classesAsOptions}
              formError={formError}
              watch={watch}
              setSelectCategory={setSelectCategory}
              selectCategory={selectCategory}
              isPublic={isPublic}
              setIsEditCoverImage={setIsEditCoverImage}
              isEditCoverImage={isEditCoverImage}
              setFileStatus={setFileStatus}
              setClassName={setClassName}
              fileStatus={fileStatus}
              className={className}
              editDocument={editDocument}
            />
            <Footer
              onButtonCancel={onClose}
              onSubmit={handleSubmit(onSubmit)}
              isDisabled={formError?.fileCode}
              isAdminLibraryLoading={isAdminLibraryLoading}
            />
          </motion.aside>
        </EditDocumentContainer>
      ) : null}
    </AnimatePresence>
  );
};
